import { ReconciliationResponse, ReconTotals } from "../api/types";
import { centsToUsd, formatCount } from "./format";

// Signed USD, e.g. "+$1,234.56" / "-$78.90". centsToUsd already renders negatives with a minus.
const signedUsd = (cents: number): string => (cents > 0 ? "+" : "") + centsToUsd(cents);
const signedCount = (n: number): string => (n > 0 ? "+" : "") + formatCount(n);

// One-sentence bottom line shown at the top of the page and the CSV.
export function reconciliationHeadline(d: ReconciliationResponse): string {
  const net =
    d.netGap === 0
      ? "Net matches exactly"
      : `Net differs by ${signedUsd(d.netGap)}`;
  return (
    `New Gross is ${signedUsd(d.grossGap)} vs the old-comparable basis — comp/$0 and refunded ` +
    `sales stay in Gross and net back out, so ${net}.`
  );
}

// CSV cell quoting (RFC-4180-ish): wrap every field in quotes, escape embedded quotes.
const q = (v: string | number): string => `"${String(v).replace(/"/g, '""')}"`;
const row = (...cells: (string | number)[]): string => cells.map(q).join(",");

const TOTAL_ROWS: { key: keyof ReconTotals; label: string; money: boolean }[] = [
  { key: "gross", label: "Gross Sales", money: true },
  { key: "net", label: "Net Sales", money: true },
  { key: "tax", label: "Tax", money: true },
  { key: "tip", label: "Tips", money: true },
  { key: "surcharge", label: "Service Fee", money: true },
  { key: "discount", label: "Discounts", money: true },
  { key: "refunds", label: "Refunds", money: true },
  { key: "orders", label: "Orders", money: false },
  { key: "transactions", label: "Transactions", money: false },
];

// Assembles the whole reconciliation as one CSV: methodology header + all four sections.
export function buildReconciliationCsv(d: ReconciliationResponse): string {
  const fmt = (v: number, money: boolean) => (money ? centsToUsd(v) : formatCount(v));
  const lines: string[] = [];

  const eventName = d.scope.eventName ?? "(event)";
  const legacy = d.scope.eventLegacyId != null ? ` (legacy #${d.scope.eventLegacyId})` : "";
  lines.push(row(`RECONCILIATION — ${eventName}${legacy}`));
  lines.push(row(`Window: ${d.window.from} to ${d.window.to}`));
  lines.push(row(`Day boundary: ${d.window.dayBoundary}`));
  lines.push(row(`Bottom line: ${reconciliationHeadline(d)}`));
  lines.push("");

  lines.push(row("METHODOLOGY"));
  lines.push(row("Both sides are computed on the SAME live data — the gap is pure methodology, not timing."));
  lines.push(row("Old-comparable = the legacy basis: completed, real-money-backed sales only (comp/$0 and refunded sales excluded from gross)."));
  lines.push(row("New = the corrected engine (== Event Summary): refunded and comp sales STAY in Gross; Net is capped at real money collected per order and nets the pre-tax product portion of refunds."));
  lines.push(row("Gross = Sum(order subtotal + tax). Net = real-money-capped product net. Money shown in USD."));
  lines.push("");

  lines.push(row("SIDE-BY-SIDE TOTALS"));
  lines.push(row("Metric", "Old (comparable)", "New", "Difference (New - Old)"));
  for (const r of TOTAL_ROWS) {
    const oldV = d.oldComparable[r.key];
    const newV = d.new[r.key];
    const diff = newV - oldV;
    lines.push(row(r.label, fmt(oldV, r.money), fmt(newV, r.money), r.money ? signedUsd(diff) : signedCount(diff)));
  }
  lines.push("");

  lines.push(row("ITEMIZED DIFFERENCES (Sum of Gross Delta = Gross gap)"));
  lines.push(row("Cause", "Gross Delta", "Rule", "Net impact", "Orders", "Transactions"));
  for (const diff of d.differences) {
    lines.push(row(diff.label, signedUsd(diff.grossDelta), diff.rule, signedUsd(diff.netImpact), signedCount(diff.orders), signedCount(diff.transactions)));
  }
  lines.push(row("TOTAL (Gross gap / Net gap)", signedUsd(d.grossGap), "", signedUsd(d.netGap), "", ""));
  lines.push("");

  lines.push(row("GROSS -> NET BRIDGE (New)"));
  lines.push(row("Component", "Amount", "Running"));
  const b = d.bridge;
  let running = b.gross;
  lines.push(row("Gross", centsToUsd(b.gross), centsToUsd(running)));
  const steps: [string, number][] = [
    ["- Tax", b.tax],
    ["- Discounts", b.discount],
    ["- Refund pre-tax netting", b.refundPreTaxNetting],
    ["- Real-money cap reduction", b.realMoneyCapReduction],
    ["- Header vs line adjustment", b.headerVsLineAdjustment],
  ];
  for (const [label, amt] of steps) {
    running -= amt;
    lines.push(row(label, centsToUsd(amt), centsToUsd(running)));
  }
  lines.push(row("= Net", centsToUsd(b.net), centsToUsd(b.net)));
  lines.push("");

  lines.push(row("EXTRA TRANSACTIONS (New counts that old-comparable drops)"));
  lines.push(row("Bucket", "Count", "Value"));
  const e = d.extra;
  lines.push(row("Comp / no real money", formatCount(e.compNoRealMoneyCount), centsToUsd(e.compNoRealMoneyValue)));
  lines.push(row("Refunded-order transactions", formatCount(e.refundedOrderCount), centsToUsd(e.refundedOrderValue)));
  lines.push(row("Total extra", formatCount(e.totalCount), centsToUsd(e.totalValue)));

  return lines.join("\r\n");
}

// Triggers a client-side download of the reconciliation CSV.
export function downloadReconciliationCsv(d: ReconciliationResponse): void {
  const csv = buildReconciliationCsv(d);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const name = (d.scope.eventName ?? "event").replace(/[^a-z0-9]+/gi, "_");
  a.href = url;
  a.download = `Reconciliation_${name}_${d.window.from}_to_${d.window.to}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
